import type { MouseEventHandler } from 'react';
import type React from 'react';
import type { DefaultTheme } from 'styled-components';
import { styled, useTheme } from 'styled-components';

import { Media } from '../../../theme/Media.tsx';
import Icons from '../../Elements/Icons/index.ts';
import Typography from '../../Typography/index.ts';
import type { NotificationProps, NotificationType } from './types.ts';

export const getColorByType = (theme: DefaultTheme, type?: string): string => {
  let color;
  switch (type) {
    case 'success':
      color = theme.colors.positive120;
      break;
    case 'error':
      color = theme.colors.critical120;
      break;
    case 'warning':
      color = theme.colors.warning120;
      break;
    default:
      color = theme.colors.link120;
  }
  return color;
};

const CloseIconWrapper = styled.div`
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.grey00_1};
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: auto;
`;

const CloseIconOuterWrapper = styled.div`
  padding-left: 16px;
`;

export const CloseButton: React.FC<{ closeToast: MouseEventHandler<HTMLDivElement> }> = ({ closeToast }) => {
  const theme = useTheme();
  return (
    <CloseIconOuterWrapper>
      <CloseIconWrapper onClick={closeToast}>
        <Icons.Close size={14} color={theme.colors.black_1} />
      </CloseIconWrapper>
    </CloseIconOuterWrapper>
  );
};

const IconWrapper = styled.div`
  flex-basis: 20px;
  height: 20px;
`;

export const InfoIcon: React.FC<{ type?: NotificationType }> = ({ type }) => {
  const theme = useTheme();
  const color = getColorByType(theme, type);
  return (
    <IconWrapper>
      {type === 'warning' && <Icons.Warning filled size={20} color={color} />}
      {type === 'error' && <Icons.Clear filled size={20} color={color} />}
      {type !== 'warning' && type !== 'error' && <Icons.Info filled size={20} color={color} />}
    </IconWrapper>
  );
};

const Wrapper = styled.div`
  flex: auto;
`;

const TextMedium = styled(Typography.TextMedium)<{ $maxHeight?: number }>`
  ${({ $maxHeight }) =>
    $maxHeight &&
    `
      ${`@media ${Media.mlMax}`} {
    max-height: ${$maxHeight}px;
    overflow-y: auto;
  `}
`;

interface MessageProps extends NotificationProps {
  maxHeight?: number;
}

export const Message: React.FC<MessageProps> = ({ title, body, type, maxHeight }) => {
  const theme = useTheme();
  return (
    <Wrapper data-testid='notification-message'>
      {title && (
        <Typography.TextLarge color={getColorByType(theme, type)}>
          <b data-testid='title'>{title}</b>
        </Typography.TextLarge>
      )}
      <TextMedium as='div' color={getColorByType(theme, type)} data-testid='body' $maxHeight={maxHeight}>
        {title ? body : <b data-testid='body'>{body}</b>}
      </TextMedium>
    </Wrapper>
  );
};
